// 청구서 납부 과정 > 2. 결제 대기 중

import { rootStore } from "@/mobx/store";
import { observer } from "mobx-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { ChevronLeft } from "@/components/icons/chevron";
import { Page_settings } from "@/config/page_settings";
import colors from "@/design/colors";
import fonts from "@/design/typography";
import { paymentLink, paymentLinkQuery } from "../paymentLink";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */
type PaymentMethod = "CARD" | "NAVER_PAY" | "KAKAO_PAY" | null;

/* **********************************
 * CONSTANTS
 * 상수 정의
 ********************************** */
// 폴링 주기
const POLL_INTERVAL_MS = 2000;
// 폴링 최대 대기 시간. 이 시간을 넘으면 승인 신호가 오지 않은 것으로 보고 안내 화면으로 전환한다.
// (카드 실패·사용자 이탈 시 나이스는 콜백을 보내지 않아 상태가 STORE_REQUEST 로 남는다)
const POLL_TIMEOUT_MS = 3 * 60 * 1000;
// 결제가 성사되지 않은 것이 확실한 상태. 실패 화면(재결제 안내)으로 보내도 안전하다.
const FAILED_STATUSES = [
  "APPROVE_FAIL",
  "CANCEL_COMPLETE",
  "STORE_DELETE",
];
// 승인이 살아 있을 수 있어 재결제를 유도하면 안 되는 상태.
// CANCEL_FAIL 은 승인 실패 후 망상취소까지 실패한 경우로, 카드사에는 승인이 남아
// 실제 출금됐을 수 있다(서버도 이 상태를 approveStatus=APPROVE 로 취급한다).
const UNCERTAIN_STATUSES = ["CANCEL_FAIL"];

const PaymentProcessPage = observer(() => {
  /* **********************************
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ********************************** */
  const info = rootStore.getPaymentInfo;

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  const navigate = useNavigate();

  const context: any = useContext(Page_settings);

  // 결제 결과를 확정하지 못한 상태(제한 시간 초과 또는 승인이 살아 있을 수 있는 상태).
  // 이때는 재결제를 유도하면 안 된다.
  const [uncertain, setUncertain] = useState(false);
  const intervalRef = useRef(null); // 인터벌 ID 저장
  const deadlineRef = useRef(0); // 폴링 마감 시각(ms)
  const checkingRef = useRef(false); // 느린 응답 중 중복 상태조회 방지

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  //결제완료확인
  const checkPaymentComplete = () => {
    const link = paymentLink();

    if (checkingRef.current) {
      return;
    }

    checkingRef.current = true;

    context.post(
      `/payment/getPaymentComplete`,
      {
        txId: link.txId,
        accessToken: link.accessToken,
      },
      (response) => {
        checkingRef.current = false;

        if(!response || !response.processStatus){
          if (Date.now() >= deadlineRef.current) {
            stopPolling();
            setUncertain(true);
          }
          return;
        }
        if(response.processStatus === "APPROVE_COMPLETE"){
          stopPolling();
          navigate("/payments/result");
        }else if(UNCERTAIN_STATUSES.indexOf(response.processStatus) !== -1){
          // 승인이 살아 있을 수 있다 → 실패 화면(재결제 유도)으로 보내지 않는다
          stopPolling();
          setUncertain(true);
        }else if(FAILED_STATUSES.indexOf(response.processStatus) !== -1){
          stopPolling();
          navigate("/payments/error?" + paymentLinkQuery(link));
        }else if(Date.now() >= deadlineRef.current){
          // 앱에서 오래 머물다 복귀했어도 서버의 최신 상태를 한 번 확인한 뒤에만
          // 미확정 화면을 보여준다. 승인 완료를 확인하지 않고 타임아웃부터 띄우면
          // 실제 결제가 끝난 사용자에게 실패처럼 보일 수 있다.
          stopPolling();
          setUncertain(true);
        }
        // STORE_REQUEST: 아직 승인 신호 전 → 다음 폴링에서 재확인
      },
      // 일시적인 통신 오류로 알림이 반복되지 않도록 삼키고 다음 폴링에서 재시도한다
      () => {
        checkingRef.current = false;
        if (Date.now() >= deadlineRef.current) {
          stopPolling();
          setUncertain(true);
        }
      },
      // 2초마다 전역 로딩바가 깜빡이지 않도록 진행바를 끈다
      false
    );
  };

  const startPolling = () => {
    stopPolling();
    checkingRef.current = false;
    setUncertain(false);
    deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    // 인터벌을 먼저 건다. 첫 확인이 예외로 죽더라도 폴링과 제한 시간이 살아 있어야
    // 화면이 다시 무한 대기에 빠지지 않는다.
    intervalRef.current = setInterval(checkPaymentComplete, POLL_INTERVAL_MS);
    checkPaymentComplete(); // 첫 확인은 즉시
  };

  useEffect(() => {
    startPolling();

    // 카드/간편결제 앱에서 브라우저로 돌아오는 즉시 최신 상태를 재조회한다.
    // 모바일 브라우저는 백그라운드 타이머를 멈추므로 interval만으로는 복귀 시점의
    // 승인 완료를 바로 반영하지 못할 수 있다.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startPolling();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPolling();
    };
  }, []);

  /* **********************************
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ********************************** */

  return (
    <Container>
      <Wrapper>
        {/* 헤더 */}
        <TopBar>
          <BackButton onClick={() => navigate(-1)}>
            <ChevronLeft width="28px" height="28px" />
          </BackButton>
          <TitleText>결제하기</TitleText>
          <Spacer /> {/* 가운데 정렬용 */}
        </TopBar>

        {/* 컨텐츠 */}

        <ContentWrapper>
          <ContentWrapper>
            {uncertain ? (
              <>
                <ContentTitleText>결제 결과를 확인하지 못했어요</ContentTitleText>
                <ContentSubTitleText>
                  카드 결제가 이미 완료되었을 수 있습니다.
                </ContentSubTitleText>
                <ContentSubTitleText>
                  중복 결제될 수 있으니 다시 결제하지 마시고,
                </ContentSubTitleText>
                <ContentSubTitleText>
                  아래에서 결제 상태를 다시 확인해 주세요.
                </ContentSubTitleText>
                <ContentSubTitleText>
                  확인이 계속 안 되면 가맹점에 문의해 주세요.
                </ContentSubTitleText>
                <RetryButton onClick={startPolling}>
                  결제 상태 다시 확인하기
                </RetryButton>
              </>
            ) : (
              <>
                <ContentTitleText>진행중입니다</ContentTitleText>
                <ContentSubTitleText>잠시만 기다려주세요.</ContentSubTitleText>
                <LoadingSpinner>
                  <img
                    src="/assets/image/spinner.gif"
                    alt="loading"
                    width={48}
                    height={48}
                  />
                </LoadingSpinner>
              </>
            )}
          </ContentWrapper>
        </ContentWrapper>
      </Wrapper>
    </Container>
  );
});

export default PaymentProcessPage;

// 페이지 전체
const Container = styled.div`
  max-width: 600px;
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
  box-sizing: border-box;
  margin: 0 auto;
  overflow-y: auto;
`;

// 페이지 전체 > 래퍼
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
`;

const TopBar = styled.div`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.gray50};
  padding: 0 20px;
`;

const BackButton = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  min-height: calc(
    100vh - 48px - 90px
  ); /* 전체 높이에서 TopBar(48px)와 FooterWrapper(90px) 높이를 뺌 */
`;

const TitleText = styled.div`
  flex: 1;
  text-align: center;
  font-family: ${fonts.body1SemiBold.fontFamily};
  font-size: ${fonts.body1SemiBold.fontSize};
  line-height: ${fonts.body1SemiBold.lineHeight};
  color: ${colors.gray900};
`;

const ContentTitleText = styled.div`
  text-align: center;
  font-family: ${fonts.heading3SemiBold.fontFamily};
  font-size: ${fonts.heading3SemiBold.fontSize};
  line-height: ${fonts.heading3SemiBold.lineHeight};
  font-weight: 600;
  color: ${colors.gray900};
`;

const ContentSubTitleText = styled.div`
  font-family: ${fonts.body3Regular.fontFamily};
  font-size: ${fonts.body3Regular.fontSize};
  line-height: ${fonts.body3Regular.lineHeight};
  font-weight: 400;
  font-size: 14px;
  color: ${colors.gray900};
`;

const LoadingSpinner = styled.div`
  margin-top: 16px;
`;

const RetryButton = styled.div`
  margin-top: 24px;
  padding: 14px 24px;
  border-radius: 8px;
  background-color: ${colors.blue500};
  color: ${colors.white};
  font-family: ${fonts.body1SemiBold.fontFamily};
  font-size: ${fonts.body1SemiBold.fontSize};
  line-height: ${fonts.body1SemiBold.lineHeight};
  cursor: pointer;
`;

const Spacer = styled.div`
  width: 28px; /* BackButton과 같은 너비 */
`;
